import * as React from "react";
import { useEffect, useState } from "react";
import {
  formatUsPhoneNumber,
  isValidUsPhoneNumber,
  normalizeUsPhoneNumber,
  toUsE164Phone,
  isValidEmail,
  normalizeEmail,
} from "@constructorfleet/utils";
import { Button } from "./primitives";
import { TextInput } from "./primitives";

export { formatUsPhoneNumber, normalizeUsPhoneNumber, toUsE164Phone, isValidEmail, normalizeEmail };

// ---------------------------------------------------------------------------
// Select
// ---------------------------------------------------------------------------
type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>;

export function Select({ className = "", ...props }: SelectProps) {
  return <select className={["ds-input", className].filter(Boolean).join(" ")} {...props} />;
}

// ---------------------------------------------------------------------------
// PhoneInput
// ---------------------------------------------------------------------------
type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "inputMode" | "value" | "onChange"
> & {
  value?: string;
  onChange?: (value: string) => void;
  valueFormat?: "national" | "e164";
  validateOnChange?: boolean;
  onValidation?: (isValid: boolean) => void;
};

export function PhoneInput({
  value = "",
  onChange,
  valueFormat = "national",
  validateOnChange = false,
  onValidation,
  ...props
}: PhoneInputProps) {
  const [touched, setTouched] = useState(false);
  const [inputValue, setInputValue] = useState(normalizeUsPhoneNumber(String(value)));
  useEffect(() => {
    setInputValue(normalizeUsPhoneNumber(String(value)));
  }, [value]);
  const normalizedValue = normalizeUsPhoneNumber(inputValue);
  const validation = isValidUsPhoneNumber(normalizedValue);
  const showInvalid =
    touched && validateOnChange && normalizedValue.trim().length > 0 && !validation.ok;

  return (
    <TextInput
      {...props}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={formatUsPhoneNumber(normalizedValue)}
      aria-invalid={showInvalid || undefined}
      data-invalid={showInvalid || undefined}
      onChange={(event) => {
        setTouched(true);
        const raw = event.target.value;
        if (props.required === true && raw.trim().length === 0) {
          setInputValue("");
          onValidation?.(false);
          onChange?.("");
          return;
        }
        const nextValue = normalizeUsPhoneNumber(raw);
        setInputValue(nextValue);
        onChange?.(valueFormat === "e164" ? toUsE164Phone(nextValue) : nextValue);
        onValidation?.(isValidUsPhoneNumber(nextValue).ok);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// EmailInput
// ---------------------------------------------------------------------------
type EmailInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> & {
  onChange?: (value: string) => void;
  validateOnChange?: boolean;
  onValidation?: (isValid: boolean) => void;
};

export function EmailInput({
  value = "",
  onChange,
  validateOnChange = false,
  onValidation,
  ...props
}: EmailInputProps) {
  const [touched, setTouched] = useState(false);
  const [inputValue, setInputValue] = useState(String(value));
  useEffect(() => {
    setInputValue(String(value));
  }, [value]);
  const validation = isValidEmail(inputValue);
  const showInvalid = touched && validateOnChange && inputValue.trim().length > 0 && !validation.ok;

  return (
    <TextInput
      {...props}
      type="email"
      inputMode="email"
      value={inputValue}
      aria-invalid={showInvalid || undefined}
      data-invalid={showInvalid || undefined}
      onChange={(event) => {
        setTouched(true);
        const newValue = event.target.value;
        setInputValue(newValue);
        onValidation?.(isValidEmail(newValue).ok);
        onChange?.(newValue);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// TimePicker
// ---------------------------------------------------------------------------
type TimePickerProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & {
  stepMinutes?: number;
};

export function TimePicker({ className = "", stepMinutes = 1, ...props }: TimePickerProps) {
  return (
    <input
      type="time"
      step={stepMinutes * 60}
      className={["ds-input", className].filter(Boolean).join(" ")}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// MultiSelectTags
// ---------------------------------------------------------------------------
type MultiSelectTagsProps = {
  id?: string;
  values: string[];
  options: string[];
  onChange: (next: string[]) => void;
  emptyLabel?: string;
  disabled?: boolean;
};

function unique(values: string[]) {
  return [...new Set(values)];
}

function toggle(values: string[], value: string) {
  if (values.includes(value)) {
    return values.filter((item) => item !== value);
  }
  return [...values, value];
}

export function MultiSelectTags({
  id,
  values,
  options,
  onChange,
  emptyLabel = "No items selected.",
  disabled = false,
}: MultiSelectTagsProps) {
  const selected = unique(values.filter((v) => v.trim().length > 0));
  const available = unique(options.filter((v) => v.trim().length > 0 && !selected.includes(v)));

  return (
    <div id={id} className="ds-tag-select">
      <div className="ds-tag-select-selected">
        {selected.length === 0 ? <p className="ds-help">{emptyLabel}</p> : null}
        {selected.map((v) => (
          <Button
            key={`selected-${v}`}
            type="button"
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => onChange(selected.filter((item) => item !== v))}
          >
            {v} ×
          </Button>
        ))}
      </div>
      <div className="ds-tag-select-options">
        {available.map((v) => (
          <Button
            key={`option-${v}`}
            type="button"
            size="sm"
            variant={selected.includes(v) ? "primary" : "ghost"}
            disabled={disabled}
            aria-pressed={selected.includes(v)}
            onClick={() => onChange(toggle(selected, v))}
          >
            {v}
          </Button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MultiValueInput
// ---------------------------------------------------------------------------
type MultiValueInputProps = {
  id?: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  addButtonLabel?: string;
  removeButtonLabel?: string;
  itemAriaLabel?: (index: number) => string;
  disabled?: boolean;
  minItems?: number;
};

function updateListValue(values: string[], index: number, next: string) {
  return values.map((v, i) => (i === index ? next : v));
}

function removeListValue(values: string[], index: number) {
  return values.filter((_, i) => i !== index);
}

export function MultiValueInput({
  id,
  values,
  onChange,
  placeholder,
  addButtonLabel = "Add item",
  removeButtonLabel = "Remove",
  itemAriaLabel,
  disabled = false,
  minItems = 0,
}: MultiValueInputProps) {
  return (
    <div className="ds-multi-value">
      {values.map((value, index) => {
        const inputId = index === 0 ? (id ?? undefined) : id ? `${id}-${index + 1}` : undefined;
        return (
          <div className="ds-multi-value-row" key={`multi-value-${index}`}>
            <TextInput
              id={inputId}
              value={value}
              onChange={(event) =>
                onChange(updateListValue(values, index, event.currentTarget.value))
              }
              aria-label={itemAriaLabel?.(index)}
              placeholder={placeholder}
              disabled={disabled}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="btn-danger"
              disabled={disabled || values.length <= minItems}
              onClick={() => onChange(removeListValue(values, index))}
            >
              {removeButtonLabel}
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={disabled}
        onClick={() => onChange([...values, ""])}
      >
        {addButtonLabel}
      </Button>
    </div>
  );
}
